import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { StudySession } from '../../types/Study';
import { FeedbackTrigger } from '../../types/Feedback';
import { examUpdated } from '../exams/examsSlice';
import { UserExam } from '../../types/User';
import { logout } from '../auth/authSlice';

type TodaySessionStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface TodaySessionState {
  session: StudySession;
  status: TodaySessionStatus;
  error: string | null;
  lastFetchedAt: number | null;
  // Transient signal: a session-count milestone (SESSIONS_5/20/50) the backend
  // flagged on the last completion (ORA-196). SessionFeedbackProvider consumes
  // it to prompt for EXPERIENCE feedback, then clears it.
  pendingFeedbackMilestone: FeedbackTrigger | null;
}

const initialSession: StudySession = {
  id: '',
  userId: '',
  examId: '',
  date: '',
  flashcardCount: 0,
  completedFlashcardCount: 0,
  vignetteCount: 0,
  videoCount: 0,
  videoWatchedCount: 0,
  blockCount: 0,
  isComplete: false,
  isStarted: false,
  isReview: false,
  isSnoozed: false,
  isOffDay: false,
  shouldShowOffDayUI: false,
  isScheduledOffDay: false,
  studiedAnywayOnOffDay: false,
  nextStudyDate: null,
  activeBlockId: '',
  vignetteCorrectCount: 0,
  vignetteIncorrectCount: 0,
  vignetteOmittedCount: 0,
  hasSeenPriorities: false,
  isUsingFlashcards: true,
  isUsingQbank: true,
  isUsingVideos: false,
  isFirstSession: false,
};

const initialState: TodaySessionState = {
  session: initialSession,
  status: 'idle',
  error: null,
  lastFetchedAt: null,
  pendingFeedbackMilestone: null,
};

const todaySessionSlice = createSlice({
  name: 'todaySession',
  initialState,
  reducers: {
    todaySessionLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    todaySessionReceived: (
      state,
      action: PayloadAction<StudySession | null>
    ) => {
      state.session = action.payload ?? initialSession;
      state.status = 'succeeded';
      state.error = null;
      state.lastFetchedAt = Date.now();
    },
    todaySessionUpdated: (state, action: PayloadAction<StudySession>) => {
      state.session = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    todaySessionFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load today session';
    },
    pendingFeedbackMilestoneSet: (
      state,
      action: PayloadAction<FeedbackTrigger>
    ) => {
      state.pendingFeedbackMilestone = action.payload;
    },
    pendingFeedbackMilestoneCleared: (state) => {
      state.pendingFeedbackMilestone = null;
    },
    clearTodaySession: (state) => {
      state.session = initialSession;
      state.status = 'idle';
      state.error = null;
      state.lastFetchedAt = null;
      state.pendingFeedbackMilestone = null;
    },
    incrCompletedFlashcards: (state) => {
      if (state.session) {
        state.session.completedFlashcardCount += 1;
      }
    },
    decrCompletedFlashcards: (state) => {
      if (state.session) {
        const newCompletedFlashcardCount = Math.max(
          state.session.completedFlashcardCount - 1,
          0
        );

        state.session.completedFlashcardCount = newCompletedFlashcardCount;
      }
    },
    updateActiveBlockId: (state, action: PayloadAction<string>) => {
      if (state.session) {
        state.session.activeBlockId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        examUpdated,
        (state, action: PayloadAction<Partial<UserExam> & { id: string }>) => {
          if (action.payload.primary === true && state.session) {
            if (typeof action.payload.isUsingFlashcards === 'boolean') {
              state.session.isUsingFlashcards =
                action.payload.isUsingFlashcards;
            }
            if (typeof action.payload.isUsingQbank === 'boolean') {
              state.session.isUsingQbank = action.payload.isUsingQbank;
            }
            if (typeof action.payload.isUsingVideos === 'boolean') {
              state.session.isUsingVideos = action.payload.isUsingVideos;
            }
          }
        }
      )
      .addCase(logout, (state) => {
        state.session = initialSession;
        state.status = 'idle';
        state.error = null;
        state.lastFetchedAt = null;
        state.pendingFeedbackMilestone = null;
      })
      // `pendingFeedbackMilestone` is a per-completion signal, not durable state.
      // `todaySession` is persisted wholesale, so null it on rehydrate rather
      // than resurrecting a stale milestone (and re-prompting) after a reload.
      .addCase(REHYDRATE, (state) => {
        state.pendingFeedbackMilestone = null;
      });
  },
});

// Selectors
export const selectTodaySession = (state: {
  todaySession: TodaySessionState;
}) => state.todaySession.session;

export const selectTodaySessionLastFetchedAt = (state: {
  todaySession: TodaySessionState;
}) => state.todaySession.lastFetchedAt;

export const selectIsQBankComplete = createSelector(
  [selectTodaySession],
  (todaySession) => {
    // Ensure we have valid numbers to avoid NaN
    const correctCount = todaySession.vignetteCorrectCount || 0;
    const incorrectCount = todaySession.vignetteIncorrectCount || 0;
    const omittedCount = todaySession.vignetteOmittedCount || 0;
    const totalCount = todaySession.vignetteCount || 0;

    // Avoid division by zero or invalid comparisons
    if (totalCount === 0) return false;

    return correctCount + incorrectCount + omittedCount === totalCount;
  }
);

export const selectIsFlashcardsComplete = createSelector(
  [selectTodaySession],
  (todaySession) => {
    const completedCount = todaySession.completedFlashcardCount || 0;
    const totalCount = todaySession.flashcardCount || 0;

    if (totalCount === 0) return false;

    return completedCount >= totalCount;
  }
);

/**
 * Creates a selector to check if the session is complete based on all enabled content types.
 * Requires the completedVideosCount to be passed in since videos are stored in a separate slice.
 */
export const makeSelectIsSessionComplete = () =>
  createSelector(
    [
      selectTodaySession,
      selectIsQBankComplete,
      selectIsFlashcardsComplete,
      (
        _state: { todaySession: TodaySessionState },
        completedVideosCount: number
      ) => completedVideosCount,
    ],
    (
      todaySession,
      isQBankComplete,
      isFlashcardsComplete,
      completedVideosCount
    ) => {
      // Check if each enabled content type is complete

      // QBank check
      if (todaySession.isUsingQbank && todaySession.vignetteCount > 0) {
        if (!isQBankComplete) return false;
      }

      // Flashcards check
      if (todaySession.isUsingFlashcards && todaySession.flashcardCount > 0) {
        if (!isFlashcardsComplete) return false;
      }

      // Videos check
      if (todaySession.isUsingVideos && todaySession.videoCount > 0) {
        if (completedVideosCount < todaySession.videoCount) return false;
      }

      // If at least one content type has items and all are complete
      const hasContent =
        (todaySession.isUsingQbank && todaySession.vignetteCount > 0) ||
        (todaySession.isUsingFlashcards && todaySession.flashcardCount > 0) ||
        (todaySession.isUsingVideos && todaySession.videoCount > 0);

      return hasContent;
    }
  );

export const {
  todaySessionLoading,
  todaySessionReceived,
  todaySessionUpdated,
  todaySessionFailed,
  pendingFeedbackMilestoneSet,
  pendingFeedbackMilestoneCleared,
  clearTodaySession,
  incrCompletedFlashcards,
  decrCompletedFlashcards,
  updateActiveBlockId,
} = todaySessionSlice.actions;

export default todaySessionSlice.reducer;
